import React, { FC, Fragment } from 'react';
import { UseQueryResult } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography, Unstable_Grid2 as Grid } from '@mui/material';

import { CompanyDetails, AnalyticsProfile, GetEbrIntroductionQuery } from 'api';
import { useProtectedClient } from 'hooks';
import { SocialAvatar } from 'components';
import { formatStat, getAppImage } from 'helpers';
import { APP_IMAGES } from 'app-constants';
import theme from 'theme';

import { global, common, COLORS } from '../styles';
import { KeyPoint } from '../Components';

interface Props {
  queryOptions: UseQueryResult<GetEbrIntroductionQuery>;
}

const styles = {
  card: {
    padding: 4,
    textAlign: 'center',
    position: 'relative',
    height: '100%',
  },
  after: {
    '&:after': {
      display: 'block',
      position: 'absolute',
      content: '""',
      width: 64,
      height: 64,
      borderRadius: '0 0 16px 0',
      marginRight: '-55px',
      transform: 'rotate(-45deg) translateY(-50%)',
      background: `linear-gradient(-45deg, ${theme.palette.primary.main} 50%, transparent 50%)`,
      top: '50%',
      right: 0,
      zIndex: 2,
    },
  },
  cardHeader: {
    ...global.paragraph1,
    fontWeight: 800,
    color: COLORS.blue1,
    marginBottom: 4,
  },
  image: {
    width: '100%',
    height: '100%',
  },
} as const;

const AdvocacyIntroduction: FC<Props> = ({ queryOptions }) => {
  const client = useProtectedClient();
  const { t } = useTranslation();

  const { data } = queryOptions;
  const {
    top_profiles = [],
    top_companies = [],
    registered_users_count = 0,
    shares_count = 0,
  } = data?.getEbrIntroduction ?? {};

  const registeredUsersCount = formatStat(registered_users_count);
  const sharesCount = formatStat(shares_count);

  function renderProfile(profile: AnalyticsProfile, index: number) {
    return (
      <Grid key={index}>
        <SocialAvatar name={profile.name} image={profile.network_profile_image} size={48} />
      </Grid>
    );
  }

  function renderCompany(company: CompanyDetails, index: number) {
    return (
      <Grid key={index}>
        <SocialAvatar
          variant="square"
          name={company.job_company_name}
          key={index}
          image={company.company_logo}
          size={48}
          style={{ borderRadius: 4 }}
        />
      </Grid>
    );
  }

  return (
    <Fragment>
      <Typography marginBottom={1} sx={common.header}>
        {t('advocacy:introduction')}
      </Typography>
      <Typography sx={common.paragraph}>
        {t('advocacy:introText', { name: client.title })}
      </Typography>
      <Typography marginTop={6} sx={common.subheader}>
        {t('advocacy:introSubheader')}
      </Typography>
      <Box marginTop={2} marginBottom={4}>
        <Grid container alignItems="center" spacing={2}>
          <Grid xs>
            <Paper sx={{ ...styles.card, ...styles.after }}>
              <Typography sx={styles.cardHeader}>{t('advocacy:introCardHeader1')}</Typography>
              <Grid alignItems="center" justifyContent="center" container spacing={2}>
                {top_profiles.map(renderProfile)}
              </Grid>
            </Paper>
          </Grid>
          <Grid xs>
            <Paper sx={{ ...styles.card, ...styles.after }}>
              <Typography sx={styles.cardHeader}>{t('advocacy:introCardHeader2')}</Typography>
              <img
                alt={t('advocacy:shareFromAnywhere')}
                style={styles.image}
                {...getAppImage(APP_IMAGES.AdvocacyShareLogos, 200)}
              />
            </Paper>
          </Grid>
          <Grid xs>
            <Paper sx={styles.card}>
              <Typography sx={styles.cardHeader}>{t('advocacy:introCardHeader3')}</Typography>
              <Grid alignItems="center" justifyContent="center" container spacing={2}>
                {top_companies.map(renderCompany)}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      <Box marginBottom={8}>
        <Typography sx={{ ...common.subheader, fontWeight: 800 }}>
          {t('advocacy:keyPoints')}
        </Typography>
        <Box marginTop={1} marginLeft={2}>
          <KeyPoint
            text={t('advocacy:keyPointsRegisteredUsersText', {
              count: registered_users_count,
              formatted: registeredUsersCount,
            })}
          />
          <KeyPoint
            text={t('advocacy:keyPointsSharesText', {
              count: shares_count,
              formatted: sharesCount,
            })}
          />
        </Box>
      </Box>
    </Fragment>
  );
};

export default AdvocacyIntroduction;
